/**
 * Public server functions for the Audioguías (tourist side).
 *
 * These are read-only and AI-free: they return stored content only.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import type { PublicAudioGuide, GuideAvailability } from "@/lib/audio-guides.server";

const SlugSchema = z
  .string()
  .trim()
  .min(1)
  .max(180)
  .regex(/^[a-z0-9-]+$/i, "Slug inválido");

const LangSchema = z.enum(["es", "en", "fr", "it", "de", "pt"]);

export const getAudioGuide = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ tourSlug: SlugSchema, lang: LangSchema.optional() }).parse(d),
  )
  .handler(async ({ data }): Promise<{ guide: PublicAudioGuide | null }> => {
    const { getPublishedGuide } = await import("@/lib/audio-guides.server");
    const guide = await getPublishedGuide(data.tourSlug, data.lang ?? null);
    return { guide };
  });

export const getAudioGuideAvailability = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ tourSlug: SlugSchema }).parse(d))
  .handler(async ({ data }): Promise<GuideAvailability> => {
    const { getGuideAvailability } = await import("@/lib/audio-guides.server");
    return getGuideAvailability(data.tourSlug);
  });

export const trackAudioGuideEvent = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        eventType: z.enum([
          "guide_opened",
          "chapter_played",
          "chapter_completed",
          "guide_completed",
          "download",
        ]),
        tourSlug: SlugSchema.optional(),
        guideId: z.string().uuid().optional(),
        lang: LangSchema.optional(),
        chapterKey: z.string().trim().max(60).optional(),
        accessCode: z.string().trim().max(16).optional(),
        deviceId: z.string().trim().max(80).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }): Promise<{ ok: true }> => {
    const { logGuideEvent } = await import("@/lib/audio-guides.server");

    let bookingId: string | null = null;
    let otaChannel: string | null = null;
    if (data.accessCode) {
      const { findBookingByAccessCode } = await import("@/lib/experience.server");
      const booking = await findBookingByAccessCode(data.accessCode);
      if (booking) {
        bookingId = booking.id;
        otaChannel = booking.ota_channel;
      }
    }

    await logGuideEvent({
      eventType: data.eventType,
      tourSlug: data.tourSlug ?? null,
      guideId: data.guideId ?? null,
      lang: data.lang ?? null,
      chapterKey: data.chapterKey ?? null,
      bookingId,
      otaChannel,
      deviceId: data.deviceId ?? null,
    });
    return { ok: true };
  });
